/** MaaS 创建/编辑：复杂 UI 态写入 workload annotations，编辑时统一解析 */
import { cloneDeep, get, set } from 'lodash';

import type { MaasGroupSpec, MaasSharedFormValues, MaasWorkload } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MaasInferenceSource } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { parseFormJsonField, toFormJsonString } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import { parseNodesField } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import { isPersistedSystemImageMatchingCatalog } from '@/delivery-type-plugin/capabilities/maas-node-groups/payload';
import { IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigFields';
import { resolveImageContainerDeployStartupFromFormValues } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigForm';
import {
  normalizeInstanceSchedulingFromContainerDeployConfig,
  resolveContainerDeployConfigFromFormValues,
} from '@/delivery-type-plugin/capabilities/maas-system-config/deployConfigPayload';
import { parseInferenceFrameworkImageItem } from '@/delivery-type-plugin/capabilities/maas-system-config/presetInferenceImage';
import type { ModelCapabilityModalityKey } from '@/types/model-capabilities';
import { safeParseJSON } from '@/utils/json-parse';

interface MaasInferenceServiceNameItem {
  name?: string;
}

const MAAS_FORM_ANNOTATION_KEY = 'stack.io/maasForm';

const LEGACY_INFERENCE_FRAMEWORK_IMAGE = 'stack.io/inferenceFrameworkImage';
const LEGACY_PRIMARY_DATA_VOLUME = 'stack.io/primaryDataVolume';
const LEGACY_COMMON_ENV = 'stack.io/commonEnv';
const LEGACY_INFERENCE_SERVICE = 'stack.io/inferenceService';

/** 存入 annotations 的前端表单快照（无法或难以从 spec 完整还原的字段） */
export interface MaasFormAnnotationSnapshot {
  inferenceFrameworkImage?: unknown;
  primaryDataVolume?: unknown;
  commonEnv?: unknown;
  inferenceService?: unknown;
  /** 镜像 containerDeployConfig.startupCommands，编辑时追溯镜像侧预置参数（AppInstance） */
  imageContainerDeployStartup?: unknown;
  /** 镜像 containerDeployConfig.instanceScheduling，编辑时用于节点资源限制 */
  imageContainerDeployInstanceScheduling?: unknown;
  /** 应用实例数据盘挂载路径 */
  dataMountPath?: string;
  /** 应用实例：镜像回填存储卷 name 列表（如 data0、data1），用于编辑时区分镜像预设与用户自定义 */
  formsystem?: string[];
  originalApis?: unknown;
  /** 模型实例：模型能力类型（如 textGeneration、imageGeneration） */
  modelCategory?: ModelCapabilityModalityKey[];
  /** 各节点组 GPU 资源类型（API 不持久化 resourceType，按组名存快照供编辑回填） */
  groupGpuResourceTypes?: Record<string, string>;
  /** 模型实例：镜像 containerDeployConfig 完整快照（自 system.options 迁至 maasForm） */
  containerDeployConfig?: unknown;
  /** 主/Worker 系统盘计价选型（含 quantityUnit，算价 Gi→计费单位依赖） */
  masterSystemDiskPricing?: unknown;
  workerSystemDiskPricing?: unknown;
}

/** 从节点 JSON 提取各组 GPU resourceType（组名 -> resourceType） */
export function extractGroupGpuResourceTypesFromNodeFields(masterNodes: unknown, workerNodes: unknown): Record<string, string> {
  const result: Record<string, string> = {};
  const collect = (nodes: MaasGroupSpec[]) => {
    nodes.forEach((node) => {
      const name = String(node?.name ?? '').trim();
      const resourceType = String(get(node, 'resource.gpu.resourceType') ?? '').trim();
      if (name && resourceType) {
        result[name] = resourceType;
      }
    });
  };
  collect(parseNodesField(masterNodes));
  collect(parseNodesField(workerNodes));
  return result;
}

/** 将 annotation 中的 GPU resourceType 合并回节点 JSON 字段 */
export function applyGroupGpuResourceTypesToNodeFields(
  masterNodes: unknown,
  workerNodes: unknown,
  resourceTypes?: Record<string, string>,
): { masterNodes: string; workerNodes: string } {
  if (!resourceTypes || !Object.keys(resourceTypes).length) {
    const masterRaw =
      masterNodes == null || masterNodes === '' ? '[]' : typeof masterNodes === 'string' ? masterNodes : JSON.stringify(masterNodes);
    const workerRaw =
      workerNodes == null || workerNodes === '' ? '[]' : typeof workerNodes === 'string' ? workerNodes : JSON.stringify(workerNodes);
    return { masterNodes: masterRaw, workerNodes: workerRaw };
  }

  const merge = (nodes: MaasGroupSpec[]) =>
    nodes.map((node) => {
      const resourceType = resourceTypes[String(node?.name ?? '').trim()];
      if (!resourceType || !get(node, 'resource.gpu')) return node;
      const next = cloneDeep(node);
      set(next, 'resource.gpu.resourceType', resourceType);
      return next;
    });

  return {
    masterNodes: JSON.stringify(merge(parseNodesField(masterNodes))),
    workerNodes: JSON.stringify(merge(parseNodesField(workerNodes))),
  };
}

function parseAnnotationValue<T>(raw: unknown, fallback: T): T {
  if (raw == null || raw === '') return fallback;
  if (typeof raw === 'object') return raw as T;
  return safeParseJSON(String(raw), fallback);
}

function getWorkloadAnnotationMap(detail: MaasWorkload): Record<string, string> {
  const specAnn = get(detail, 'spec.annotations') as Record<string, string> | undefined;
  const metaAnn = detail.metadata?.annotations;
  return { ...(specAnn || {}), ...(metaAnn || {}) };
}

/**
 * 将 maasForm.inferenceService 与 status.network.service 对齐。
 * 单独 DELETE service 后后端可能未回写 annotation，读取时以 status 为准过滤已删除项。
 */
export function syncInferenceServiceWithNetworkStatus(detail: MaasWorkload, inferenceService: unknown): unknown {
  const statusServices = get(detail, 'status.network.service');
  if (!Array.isArray(statusServices)) return inferenceService;

  const statusNames = new Set(
    statusServices.map((item) => String((item as MaasInferenceServiceNameItem)?.name || '').trim()).filter(Boolean),
  );

  const list = parseFormJsonField<MaasInferenceServiceNameItem[]>(inferenceService);
  if (!Array.isArray(list) || !list.length) return inferenceService;

  return list.filter((item) => {
    const name = String(item?.name || '').trim();
    return name && statusNames.has(name);
  });
}

function withSyncedInferenceService(detail: MaasWorkload, snapshot: MaasFormAnnotationSnapshot): MaasFormAnnotationSnapshot {
  return {
    ...snapshot,
    inferenceService: syncInferenceServiceWithNetworkStatus(detail, snapshot.inferenceService),
  };
}

/** 从详情解析表单快照（新 key 优先，兼容旧分散 key） */
export function readMaasFormAnnotationSnapshot(detail: MaasWorkload): MaasFormAnnotationSnapshot {
  const annotations = getWorkloadAnnotationMap(detail);
  const consolidated = parseAnnotationValue<MaasFormAnnotationSnapshot>(annotations[MAAS_FORM_ANNOTATION_KEY], {});
  if (consolidated && Object.keys(consolidated).length > 0) {
    return withSyncedInferenceService(detail, consolidated);
  }

  return withSyncedInferenceService(detail, {
    inferenceFrameworkImage: parseAnnotationValue(annotations[LEGACY_INFERENCE_FRAMEWORK_IMAGE], undefined),
    primaryDataVolume: parseAnnotationValue(annotations[LEGACY_PRIMARY_DATA_VOLUME], undefined),
    commonEnv: parseAnnotationValue(annotations[LEGACY_COMMON_ENV], undefined),
    inferenceService: parseAnnotationValue(annotations[LEGACY_INFERENCE_SERVICE], undefined),
  });
}

/** 从表单值构建快照 */
export function buildMaasFormAnnotationSnapshot(
  values: Pick<
    MaasSharedFormValues,
    | 'inferenceFrameworkImage'
    | '_primaryDataVolume'
    | 'commonEnv'
    | '_inferenceService'
    | '_imageContainerDeployStartup'
    | '_imageContainerDeployInstanceScheduling'
    | '_inferenceSystem'
  > & {
    maasInferenceSource?: MaasInferenceSource;
    dataMountPath?: string;
    formsystem?: string[];
    _modelCategory?: ModelCapabilityModalityKey[];
    masterNodes?: unknown;
    workerNodes?: unknown;
    masterSystemDiskPricing?: unknown;
    workerSystemDiskPricing?: unknown;
  },
): MaasFormAnnotationSnapshot {
  const isBuiltinImage =
    values.maasInferenceSource !== MaasInferenceSource.Custom &&
    values.inferenceFrameworkImage != null &&
    values.inferenceFrameworkImage !== '';
  const containerDeployConfig = resolveContainerDeployConfigFromFormValues(values as MaasSharedFormValues);
  const snapshot: MaasFormAnnotationSnapshot = {
    ...(isBuiltinImage ? { inferenceFrameworkImage: values.inferenceFrameworkImage } : {}),
    primaryDataVolume: values._primaryDataVolume,
    commonEnv: values.commonEnv,
    inferenceService: values._inferenceService,
    originalApis: containerDeployConfig?.apis,
  };
  const imageStartup = parseFormJsonField(values._imageContainerDeployStartup);
  if (imageStartup && typeof imageStartup === 'object' && Object.keys(imageStartup).length > 0) {
    snapshot.imageContainerDeployStartup = imageStartup;
  }
  const imageInstanceScheduling = parseFormJsonField(values._imageContainerDeployInstanceScheduling);
  if (imageInstanceScheduling && typeof imageInstanceScheduling === 'object' && Object.keys(imageInstanceScheduling).length > 0) {
    snapshot.imageContainerDeployInstanceScheduling = imageInstanceScheduling;
  }
  if (values.dataMountPath?.trim()) {
    snapshot.dataMountPath = values.dataMountPath.trim();
  }
  const formsystem = Array.isArray(values.formsystem) ? values.formsystem.map((name) => String(name).trim()).filter(Boolean) : [];
  if (formsystem.length) {
    snapshot.formsystem = formsystem;
  }
  const modelCategory = Array.isArray(values._modelCategory) ? values._modelCategory.filter(Boolean) : [];
  if (modelCategory.length) {
    snapshot.modelCategory = modelCategory;
  }
  const groupGpuResourceTypes = extractGroupGpuResourceTypesFromNodeFields(values.masterNodes, values.workerNodes);
  if (Object.keys(groupGpuResourceTypes).length) {
    snapshot.groupGpuResourceTypes = groupGpuResourceTypes;
  }
  if (values.masterSystemDiskPricing != null && typeof values.masterSystemDiskPricing === 'object') {
    snapshot.masterSystemDiskPricing = values.masterSystemDiskPricing;
  }
  if (values.workerSystemDiskPricing != null && typeof values.workerSystemDiskPricing === 'object') {
    snapshot.workerSystemDiskPricing = values.workerSystemDiskPricing;
  }
  return snapshot;
}

/** 从表单值构建并序列化 spec.annotations */
export function buildMaasFormAnnotations(
  values: Pick<
    MaasSharedFormValues,
    | 'inferenceFrameworkImage'
    | '_primaryDataVolume'
    | 'commonEnv'
    | '_inferenceService'
    | '_imageContainerDeployStartup'
    | '_imageContainerDeployInstanceScheduling'
    | '_inferenceSystem'
  > & {
    maasInferenceSource?: MaasInferenceSource;
    dataMountPath?: string;
    formsystem?: string[];
    _modelCategory?: ModelCapabilityModalityKey[];
    masterNodes?: unknown;
    workerNodes?: unknown;
    masterSystemDiskPricing?: unknown;
    workerSystemDiskPricing?: unknown;
  },
): Record<string, string> {
  return {
    [MAAS_FORM_ANNOTATION_KEY]: JSON.stringify(buildMaasFormAnnotationSnapshot(values)),
  };
}

/** 将快照合并进 detailToForm 产物（_primaryDataVolume 保持表单 JSON 字符串习惯） */
export function applyMaasFormSnapshotToFormValues<T extends Record<string, unknown>>(
  formValues: T,
  snapshot: MaasFormAnnotationSnapshot,
): T {
  const next = { ...formValues } as T & MaasSharedFormValues & { dataMountPath?: string };

  if (snapshot.inferenceFrameworkImage != null) {
    next.inferenceFrameworkImage = parseAnnotationValue(
      snapshot.inferenceFrameworkImage,
      undefined,
    ) as MaasSharedFormValues['inferenceFrameworkImage'];
  }
  if (snapshot.commonEnv != null) {
    next.commonEnv = parseAnnotationValue(snapshot.commonEnv, undefined) as MaasSharedFormValues['commonEnv'];
  }
  if (snapshot.inferenceService != null) {
    next._inferenceService = parseAnnotationValue(snapshot.inferenceService, null);
  }

  const imageStartupFromSnapshot =
    snapshot.imageContainerDeployStartup != null
      ? (parseAnnotationValue(snapshot.imageContainerDeployStartup, undefined) as MaasSharedFormValues['_imageContainerDeployStartup'])
      : undefined;
  const imageStartupResolved =
    imageStartupFromSnapshot && typeof imageStartupFromSnapshot === 'object' && Object.keys(imageStartupFromSnapshot).length > 0
      ? imageStartupFromSnapshot
      : resolveImageContainerDeployStartupFromFormValues(next);
  if (imageStartupResolved) {
    next._imageContainerDeployStartup = imageStartupResolved as MaasSharedFormValues['_imageContainerDeployStartup'];
  }
  const imageInstanceSchedulingRaw =
    snapshot.imageContainerDeployInstanceScheduling != null
      ? parseAnnotationValue(snapshot.imageContainerDeployInstanceScheduling, undefined)
      : undefined;
  if (
    imageInstanceSchedulingRaw &&
    typeof imageInstanceSchedulingRaw === 'object' &&
    Object.keys(imageInstanceSchedulingRaw as Record<string, unknown>).length > 0
  ) {
    (next as Record<string, unknown>)[IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD] = imageInstanceSchedulingRaw;
  } else {
    const containerDeployConfig = resolveContainerDeployConfigFromFormValues(next);
    const instanceScheduling = normalizeInstanceSchedulingFromContainerDeployConfig(containerDeployConfig);
    if (instanceScheduling) {
      (next as Record<string, unknown>)[IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD] = toFormJsonString(instanceScheduling);
    }
  }
  if (snapshot.primaryDataVolume != null) {
    const raw = snapshot.primaryDataVolume;
    next._primaryDataVolume = typeof raw === 'string' ? raw : JSON.stringify(raw);
  }
  if (snapshot.dataMountPath) {
    next.dataMountPath = snapshot.dataMountPath;
  }
  if (Array.isArray(snapshot.modelCategory) && snapshot.modelCategory.length) {
    (next as ModelInstanceFormValuesLike)._modelCategory = snapshot.modelCategory;
  }
  if (snapshot.groupGpuResourceTypes && Object.keys(snapshot.groupGpuResourceTypes).length) {
    const mergedNodes = applyGroupGpuResourceTypesToNodeFields(
      (next as MaasSharedFormValues).masterNodes,
      (next as MaasSharedFormValues).workerNodes,
      snapshot.groupGpuResourceTypes,
    );
    (next as MaasSharedFormValues).masterNodes = mergedNodes.masterNodes;
    (next as MaasSharedFormValues).workerNodes = mergedNodes.workerNodes;
  }
  if (snapshot.masterSystemDiskPricing != null && typeof snapshot.masterSystemDiskPricing === 'object') {
    (next as Record<string, unknown>).masterSystemDiskPricing = snapshot.masterSystemDiskPricing;
  }
  if (snapshot.workerSystemDiskPricing != null && typeof snapshot.workerSystemDiskPricing === 'object') {
    (next as Record<string, unknown>).workerSystemDiskPricing = snapshot.workerSystemDiskPricing;
  }

  const persistedSystem = parseFormJsonField<Record<string, unknown>>((next as MaasSharedFormValues)._inferenceSystem);
  const catalogItem = parseInferenceFrameworkImageItem((next as MaasSharedFormValues).inferenceFrameworkImage);
  if (catalogItem && persistedSystem && !isPersistedSystemImageMatchingCatalog(persistedSystem, catalogItem)) {
    delete (next as MaasSharedFormValues).inferenceFrameworkImage;
  }

  return next as T;
}

interface ModelInstanceFormValuesLike {
  _modelCategory?: ModelCapabilityModalityKey[];
}

/** 模型实例提交：将 containerDeployConfig / 主数据盘快照 / 系统盘计价写入 spec.annotations */
export function buildModelInstanceMaasFormAnnotations(
  containerDeployConfig?: Record<string, unknown> | string,
  extras?: {
    /**
     * 主数据盘表单快照（含 pricing）。
     * 询价往返在无节点组时依赖此字段；持久化 API 由 stripBillingOnlyFieldsFromMaasAnnotations 剥离。
     */
    primaryDataVolume?: unknown;
    masterSystemDiskPricing?: unknown;
    workerSystemDiskPricing?: unknown;
  },
): Record<string, string> | undefined {
  const snapshot: MaasFormAnnotationSnapshot = {};
  if (containerDeployConfig != null && containerDeployConfig !== '') {
    snapshot.containerDeployConfig = containerDeployConfig;
  }
  if (extras?.primaryDataVolume != null && extras.primaryDataVolume !== '') {
    snapshot.primaryDataVolume = extras.primaryDataVolume;
  }
  if (extras?.masterSystemDiskPricing != null && typeof extras.masterSystemDiskPricing === 'object') {
    snapshot.masterSystemDiskPricing = extras.masterSystemDiskPricing;
  }
  if (extras?.workerSystemDiskPricing != null && typeof extras.workerSystemDiskPricing === 'object') {
    snapshot.workerSystemDiskPricing = extras.workerSystemDiskPricing;
  }
  if (!Object.keys(snapshot).length) {
    return { [MAAS_FORM_ANNOTATION_KEY]: '{}' };
  }
  return { [MAAS_FORM_ANNOTATION_KEY]: JSON.stringify(snapshot) };
}

/** 模型实例：仅从 maasForm 读取 containerDeployConfig */
export function readModelInstanceContainerDeployConfigFromWorkload(detail: MaasWorkload): unknown {
  const raw = readMaasFormAnnotationSnapshot(detail).containerDeployConfig;
  if (raw == null || raw === '') return undefined;
  return raw;
}

/**
 * 直建 / deliveryMetadata 提交前：剥离询价专用字段。
 * 系统盘计价与 primaryDataVolume 仅服务 form→spec→toForm 询价往返，不得进入最终 API annotations。
 */
export function stripBillingOnlyFieldsFromMaasAnnotations(annotations?: Record<string, string> | null): Record<string, string> | undefined {
  if (!annotations || typeof annotations !== 'object') return undefined;
  const raw = annotations[MAAS_FORM_ANNOTATION_KEY];
  if (raw == null || raw === '') return { ...annotations };

  const snapshot = parseAnnotationValue<MaasFormAnnotationSnapshot>(raw, {});
  const hasBillingOnly = 'masterSystemDiskPricing' in snapshot || 'workerSystemDiskPricing' in snapshot || 'primaryDataVolume' in snapshot;
  if (!hasBillingOnly) {
    return { ...annotations };
  }

  const { masterSystemDiskPricing: _m, workerSystemDiskPricing: _w, primaryDataVolume: _p, ...rest } = snapshot;
  return {
    ...annotations,
    [MAAS_FORM_ANNOTATION_KEY]: JSON.stringify(rest),
  };
}
